// fetches topics

import axios from 'axios'
import Vue from 'vue'
import texts from '../../assets/data/localisations.json'
import _ from "lodash";

export const state = {
  // array to populate with on created api request
  topicsList: [],
  messagesList: [],
  localMessages: [],
  // initialize empty search input
  searchValue: '',
  topicId: '',
  hasTopicResponse: false,
  messageResponse: {
    ok: false,
    icon: 'loop',
    title: 'Waiting for server to respond.',
    message: 'Please try again.'
  },
  expandedCategoryTopics: new Set(),
}

export const getters = {
  allTopics: (state) => {
    return state.topicsList
  },
  hasTopics: (state) => {
    return state.hasTopicResponse
  },
  categoryTopics: (state, getters) => {
    return getters.filteredTopics.reduce((categories, item) => {
      const category = (categories[item.category] || []);
      category.push(item);
      categories[item.category] = category;
      return categories;
    }, {});
  },
  expandedCategoryTopics: (state) => {
    return state.expandedCategoryTopics
  },
  allMessages: (state) => {
    return state.messagesList
  },
  // search value getter
  searchValue: (state) => {
    return state.searchValue
  },
  getMessageResponse: (state) => {
    return state.messageResponse
  },
  // topic filtering
  filteredTopics: (state) => {
    return state.topicsList.filter((topic) => {
      return topic.title.toLowerCase().includes(state.searchValue.toLowerCase())
    })
  },
  filterTopicById: (state) => {
    return state.topicsList.filter((topic) => {
      if (topic.id) {
        return topic.id.toLowerCase() === state.topicId.toLowerCase()
      }
    })
  },
}

// mutations always need to run synchronsly
export const mutations = {
  // save topics and messages to topics[]
  SAVE_TOPICS(state, payload) {
    state.topicsList = payload
  },
  UPDATE_TOPIC_STATE(state, payload) {
    state.hasTopicResponse = payload
  },
  SAVE_MESSAGES(state, payload) {
    state.messagesList = state.localMessages.concat(payload)
  },
  SAVE_MESSAGE_RESPONSE(state, payload) {
    state.messageResponse = payload
  },
  INJECT_LOCAL_MESSAGE(state, payload) {
    if (payload.local) {
      state.localMessages.push(payload)
    } else {
      state.messagesList.push(payload)
    }
  },
  // mutates value from the search input
  updateSearchValue: (state, payload) => {
    state.searchValue = payload
  },
  selectedTopicId: (state, payload) => {
    state.topicId = payload
  },
  updateExpandedCategoryTopics: (state, payload) => {
    if (state.expandedCategoryTopics.has(payload)) state.expandedCategoryTopics.delete(payload)
    else state.expandedCategoryTopics.add(payload)
  }
}

export const actions = {
  // fetch topics
  fetchTopics: ({
    commit,
    rootState
  }, payload) => {
    const faqEndpoint = rootState.globals.params.minimalRequest ? 'faqMinimal' : 'faq'
    return axios({
        method: 'get',
        url: rootState.globals.hostname + '/rest/user-support/v1/' + faqEndpoint,
        params: _.omit(rootState.globals.params, 'minimalRequest'),
      })
      .then((response) => {
        // commit to mutation & update app status
        commit('SAVE_TOPICS', response.data)
        commit('UPDATE_TOPIC_STATE', true)
        commit('globals/UPDATE_STATUS', 'success', {
          root: true,
        })
      })
      .catch(function (error) {
        console.log(error)
        commit('globals/UPDATE_STATUS', 'error', {
          root: true,
        })
        Vue.$plausible.trackEvent('Error', {
          props: {
            eventCategory: 'Error',
            eventAction: 'Request failed',
            eventLabel: 'Fetch topics failed'
          }
        })
      })
  },

  // fetch conversations
  fetchMessages: ({
    commit,
    rootState
  }, payload) => {
    return axios({
        method: 'get',
        url: rootState.globals.hostname + '/rest/user-support/v1/conversation',
        params: rootState.globals.params,
      })
      .then((response) => {
        // commit to mutation
        commit('SAVE_MESSAGES', response.data)
        // find if there are unread messages and update the unread count
        let unread = 0
        response.data.forEach((value, index) => {
          if (!value.messageRead) {
            unread++
          }
          if (value.consentPopupType) {
            // save consent popup type
            commit('globals/SAVE_CONSENT_POPUP_TYPE', value.consentPopupType, {
              root: true,
            })
          }
        })
        // save unread count
        commit('globals/SAVE_UNREAD_COUNT', unread, {
          root: true,
        })
        // update app status
        commit('globals/UPDATE_STATUS', 'success', {
          root: true,
        })
        // update last fetched conversation timestamp
        commit('globals/UPDATE_CONV_TIMESTAMP', Date.now(), {
          root: true,
        })
      })
      .catch(function (error) {
        console.log(error)
        commit('globals/UPDATE_STATUS', 'error', {
          root: true,
        })
        Vue.$plausible.trackEvent('Error', {
          props: {
            eventCategory: 'Error',
            eventAction: 'Request failed',
            eventLabel: 'Fetch topics failed'
          }
        })
      })
  },
  // post message
  postMessage: ({
    commit,
    rootState,
    dispatch,
    rootGetters
  }, payload) => {
    return axios({
        method: 'post',
        url: rootState.globals.hostname + '/rest/user-support/v1/message',
        params: rootState.globals.params,
        data: payload,
      })
      .then((res) => {
        // NOTIFY USER MESSAGE WAS SENT
        if (res.status >= 200 && res.status < 300) {
          Vue.$plausible.trackEvent('Send', {
            props: {
              eventCategory: 'Send',
              eventAction: 'Message',
              eventLabel: 'Success'
            }
          })
          const response = {
            ok: true,
            title: '',
            message: texts[rootGetters['globals/locale']].supportresponse
          }
          commit('SAVE_MESSAGE_RESPONSE', response)
          console.log('Message Sent')
          // dispatch a new request for messages after 15 seconds (auto reply should be generated on server after 10 seconds)
          setTimeout(() => dispatch('topics/fetchMessages', null, {
            root: true,
          }), 15000)
        }
      })
      .catch(function (error) {
        Vue.$plausible.trackEvent('Send', {
          props: {
            eventCategory: 'Send',
            eventAction: 'Message',
            eventLabel: 'Failed'
          }
        })
        const response = {
          ok: false,
          title: '',
          message: texts[rootGetters['globals/locale']].notsent
        }
        commit('SAVE_MESSAGE_RESPONSE', response)
        console.log(error)
      })
  },
  // post consent
  postConsent: ({
    commit,
    rootState,
    dispatch,
    rootGetters
  }, payload) => {
    return axios({
        method: 'post',
        url: rootState.globals.hostname + '/rest/user-support/v1/consent',
        params: rootState.globals.params,
        data: payload,
      })
      .then((res) => {
        // NOTIFY USER MESSAGE WAS SENT
        if (res.status >= 200 && res.status < 300) {
          Vue.$plausible.trackEvent('Send', {
            props: {
              eventCategory: 'Send',
              eventAction: 'Consent',
              eventLabel: 'Success'
            }
          })
          console.log('Consent Sent')
        }
      })
      .catch(function (error) {
        Vue.$plausible.trackEvent('Send', {
          props: {
            eventCategory: 'Send',
            eventAction: 'Consent',
            eventLabel: 'Success'
          }
        })
        console.log(error)
      })
  },
  // send last read event
  markAsRead: ({
    commit,
    rootState
  }, payload) => {
    // only do it once!
    if (!rootState.globals.markedAsRead) {
      let qp = rootState.globals.params
      qp.lMT = payload
      return axios({
          method: 'get',
          url: rootState.globals.hostname + '/rest/user-support/v1/message/read',
          params: qp,
        })
        .then((res) => {
          // NOTIFY USER MESSAGE WAS SENT
          if (res.status >= 200 && res.status < 300) {
            commit('globals/MARK_READ', true, {
              root: true,
            })

            console.log('Last Message marked as read')
          }
        })
        .catch(function (error) {
          console.log(error)
          Vue.$plausible.trackEvent('Error', {
            props: {
              eventCategory: 'Error',
              eventAction: 'Request failed',
              eventLabel: 'Mark as read'
            }
          })
        })
    }
  },
  // post local message to conversation list
  postLocalMessage({
    commit
  }, payload) {
    commit('INJECT_LOCAL_MESSAGE', payload)
  },
  // updates search value
  updateSearchValue({
    commit
  }, payload) {
    commit('updateSearchValue', payload)
  },
  // updates topicId
  updateTopicId({
    commit
  }, payload) {
    commit('selectedTopicId', payload)
  },
  // updates expanded topics
  updateExpandedCategoryTopics({
    commit
  }, payload) {
    commit('updateExpandedCategoryTopics', payload)
  },
  resetMessageResponse({
    commit
  }, payload) {
    const response = {
      ok: null,
      icon: 'loop',
      title: 'Waiting for server to respond.',
      message: 'Please try again.'
    }
    commit('SAVE_MESSAGE_RESPONSE', response)
  },
  lastMessageNotSent({
    commit
  }, payload) {
    let messages = payload
    messages[messages.length - 1].error = true
    commit('SAVE_MESSAGES', messages)
  },
}
